import { useMemo } from 'react';
import { DashboardLayouts, LayoutsWithWidgetData, Widget } from './dashboardLayoutTypes';

/* ============================================================================
 * DASHBOARD LAYOUT REACT HOOK
 * React hook wrapper for dashboard layout service
 * ============================================================================ */

interface DashboardLayoutServiceInstance {
  getLayouts: () => Promise<LayoutsWithWidgetData>;
  saveLayout: (viewMode: string, layouts: DashboardLayouts, widgetData?: Widget[]) => Promise<boolean>;
  stageLayout: (viewMode: string, layouts: DashboardLayouts, widgetData?: Widget[]) => void;
  resetLayout: (viewMode: string) => Promise<boolean>;
  syncPendingChanges: () => Promise<void>;
  hasPendingChanges: () => boolean;
  getSyncStatus: () => 'synced' | 'pending' | 'offline';
  clearCache: () => void;
  isLayoutSyncedLocally: (viewMode: string, layoutId: string) => boolean;
  getNamedLayouts: (viewMode: string) => Promise<NamedLayout[]>;
  saveNamedLayout: (viewMode: string, name: string, layouts: DashboardLayouts, isDefault?: boolean, widgetData?: Widget[]) => Promise<NamedLayout | null>;
  updateNamedLayout: (viewMode: string, layoutId: string, name: string, layouts: DashboardLayouts, isDefault?: boolean, widgetData?: Widget[]) => Promise<NamedLayout | null>;
  loadNamedLayout: (viewMode: string, layoutId: string) => Promise<DashboardLayouts | null>;
  deleteNamedLayout: (viewMode: string, layoutId: string) => Promise<boolean>;
  setDefaultLayout: (viewMode: string, layoutId: string) => Promise<boolean>;
}

// Import types for the hook
import type { NamedLayout } from './dashboardLayoutTypes';

/**
 * React hook for easy integration with dashboard layout service
 */
export function useDashboardLayoutService(service: DashboardLayoutServiceInstance) {
  return useMemo(
    () => ({
      getLayouts: () => service.getLayouts(),
      saveLayout: (viewMode: string, layouts: DashboardLayouts, widgetData?: Widget[]) =>
        service.saveLayout(viewMode, layouts, widgetData),
      stageLayout: (viewMode: string, layouts: DashboardLayouts, widgetData?: Widget[]) =>
        service.stageLayout(viewMode, layouts, widgetData),
      resetLayout: (viewMode: string) => service.resetLayout(viewMode),
      syncPendingChanges: () => service.syncPendingChanges(),
      hasPendingChanges: () => service.hasPendingChanges(),
      getSyncStatus: () => service.getSyncStatus(),
      clearCache: () => service.clearCache(),
      isLayoutSyncedLocally: (viewMode: string, layoutId: string) =>
        service.isLayoutSyncedLocally(viewMode, layoutId),

      // Named layout methods
      getNamedLayouts: (viewMode: string) => service.getNamedLayouts(viewMode),
      saveNamedLayout: (viewMode: string, name: string, layouts: DashboardLayouts, isDefault?: boolean, widgetData?: Widget[]) =>
        service.saveNamedLayout(viewMode, name, layouts, isDefault, widgetData),
      updateNamedLayout: (viewMode: string, layoutId: string, name: string, layouts: DashboardLayouts, isDefault?: boolean, widgetData?: Widget[]) =>
        service.updateNamedLayout(viewMode, layoutId, name, layouts, isDefault, widgetData),
      loadNamedLayout: (viewMode: string, layoutId: string) =>
        service.loadNamedLayout(viewMode, layoutId),
      deleteNamedLayout: (viewMode: string, layoutId: string) =>
        service.deleteNamedLayout(viewMode, layoutId),
      setDefaultLayout: (viewMode: string, layoutId: string) =>
        service.setDefaultLayout(viewMode, layoutId),
    }),
    [service]
  );
}
